<template>
	<div class="video-js-container" :class="{ 'hide-big-play-button': hideBigPlayButton }">
		<video ref="videoPlayer" class="video-js vjs-default-skin" preload="auto" autoplay controls playsinline>
			<source :src="videoSrc" type="video/mp4" />
		</video>
	</div>
</template>

<script setup lang="ts">
import 'videojs-ima';
import 'videojs-ima/dist/videojs.ima.css';
import 'video.js/dist/video-js.min.css';

import { onBeforeUnmount, onMounted, ref } from 'vue';

import videojs from 'video.js';
import canAutoplay from 'can-autoplay';
import { VideojsImaHelper } from '@/helpers/videojs-ima-helper';
import { VideoJsEvents } from '@/interfaces/components/videojs-ima-player';
import type { VideoJsPlayerStatus, VideoJsImaPlayer } from '@/interfaces/components/videojs-ima-player';

import { AdEvent } from '@/enums/ima-ad-event';

withDefaults(defineProps<{ videoSrc?: string }>(), { videoSrc: '' });

const emit = defineEmits<{
	(e: 'stateChange', playerStatus: VideoJsPlayerStatus): void;
}>();

const { jwConfig } = useRuntimeConfig().public;

const videoRef = ref<HTMLVideoElement | null>(null);
const player = ref<VideoJsImaPlayer | null>(null);
const hideBigPlayButton = ref<boolean>(true);

onMounted(async () => {
	if (videoRef.value == null) return;

	await VideojsImaHelper.init();

	const muted = !(await canAutoplay.video({ timeout: 100, muted: false }));
	player.value = videojs(videoRef.value, { muted });

	player.value.on(VideoJsEvents, onStateChange);

	if (VideojsImaHelper.gadsScriptsStatus === 'loaded') {
		player.value.ima({
			id: videoRef.value,
			adTagUrl: jwConfig.AD_TAG,
			adsManagerLoadedCallback: () =>
				VideojsImaHelper.adsManagerLoadedCallback(player.value as VideoJsImaPlayer, onAdEvent),
		});
	}

	// Show play button once Player is ready
	player.value?.on('ready', () => setTimeout(() => (hideBigPlayButton.value = false), 4000));
});

onBeforeUnmount(() => {
	if (player.value == null) return;

	VideoJsEvents.forEach(event => player.value?.off(event));
	player.value.dispose();
});

const adsStatus = ref<AdEvent>();
const playerStatus = ref<VideoJsPlayerStatus>();

function onStateChange(event: { type: VideoJsPlayerStatus }) {
	playerStatus.value = event.type;

	if (VideojsImaHelper.isAdsNotPlaying(event.type, adsStatus.value)) {
		emit('stateChange', event.type);
	}
}

function onAdEvent(event: { type: AdEvent }) {
	adsStatus.value = event.type;
}
</script>

<style scoped lang="scss">
.video-js-container {
	height: 100%;
	width: 100%;
	&.hide-big-play-button {
		.vjs-big-play-button {
			display: none;
		}
	}
}
.video-js {
	height: 100%;
	width: 100%;
}
</style>
